import type { Table } from '@tanstack/react-table';

/**
 * Convert a TanStack table's current visible rows into a CSV string, then
 * trigger a download. Honors column visibility and current sorting / filters.
 */
export function exportTableAsCsv<TData>(table: Table<TData>, filename = 'export.csv'): void {
  const visibleColumns = table.getAllLeafColumns().filter((c) => c.getIsVisible() && c.id !== 'select');
  const header = visibleColumns.map((c) => csvEscape(headerText(c.columnDef.header) ?? c.id));
  const rows = table.getFilteredRowModel().rows.map((row) =>
    visibleColumns.map((col) => {
      const value = row.getValue(col.id);
      return csvEscape(stringify(value));
    }),
  );
  const csv = [header, ...rows].map((line) => line.join(',')).join('\n');
  triggerDownload(csv, filename);
}

function headerText(h: unknown): string | undefined {
  return typeof h === 'string' ? h : undefined;
}

function stringify(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return value.toISOString();
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function triggerDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
